import { useCallback, useRef, useState } from "react";

import {
  Box,
  BoxProps,
  Flex,
  Icon,
  IconButton,
  Image,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Tooltip,
  useDisclosure
} from "@chakra-ui/react";

import {
  MdClose,
  MdDownload,
  MdRotateLeft,
  MdRotateRight
} from "react-icons/md";

interface Props {
  imageUrl: string;
  imageDownloadUrl: string;
  frameHeight?: BoxProps["height"];
}

export const PreviewFile = ({
  frameHeight = "full",
  imageDownloadUrl,
  imageUrl
}: Props) => {
  const [rotation, setRotation] = useState(0);
  const [imageAspectRatio, setImageAspectRatio] = useState<number>(1);
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Extract image type early for use in handlers
  const match = imageUrl?.match(/\.([^./?#]+)($|\?|#)/);
  const imageType = match ? match[1] : "";
  const isImage = !["pdf"].includes(imageType.toLowerCase());

  const handleRotateLeft = () => {
    setRotation((prev) => (prev - 90) % 360);
  };

  const handleRotateRight = () => {
    setRotation((prev) => (prev + 90) % 360);
  };

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = imageDownloadUrl;
    link.download = `image.${imageType}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleImageLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
    const img = event.target as HTMLImageElement;
    setImageAspectRatio(img.naturalWidth / img.naturalHeight);
  };

  const getScaleAndTransform = useCallback(() => {
    const isRotated90or270 = Math.abs(rotation % 180) === 90;

    if (isRotated90or270) {
      // When rotated 90° or 270°, calculate scale based on aspect ratio
      let scale = 0.75; // Base scale for rotated images (Dont change this pls)

      if (imageAspectRatio > 2) {
        scale = 0.5;
      } else if (imageAspectRatio > 1.5) {
        scale = 0.6;
      } else if (imageAspectRatio < 0.7) {
        scale = 0.85;
      }

      return {
        transform: `rotate(${rotation}deg) scale(${scale})`,
        transformOrigin: "center",
        transition: "transform 0.3s ease-in-out"
      };
    }

    return {
      transform: `rotate(${rotation}deg)`,
      transformOrigin: "center",
      transition: "transform 0.3s ease-in-out"
    };
  }, [rotation, imageAspectRatio]);

  // Calculate zoom transform (no scaling, just rotation)
  const getZoomTransform = useCallback(
    () => ({
      transform: `rotate(${rotation}deg)`,
      transformOrigin: "center",
      transition: "transform 0.3s ease-in-out"
    }),
    [rotation]
  );

  if (!imageUrl) return null;

  if (!isImage)
    return (
      <Box bg="white" h="full" overflow="hidden" w="full" pos="relative">
        <Box
          as="iframe"
          w="full"
          h={frameHeight}
          loading="lazy"
          title="PDF Document"
          src={`${imageUrl}#navpanes=0&scrollbar=0&view=FitH&view=FitV`}
        />
      </Box>
    );

  return (
    <>
      <Box
        h="full"
        bg="white"
        w="full"
        rounded={6}
        border="1px solid"
        borderColor="gray.200"
        p={3}
        pos="relative"
        overflow="hidden"
      >
        <Flex
          position="absolute"
          top={5}
          right={5}
          bg="white"
          p={1}
          borderRadius="md"
          zIndex={10}
          boxShadow="md"
        >
          <Tooltip label="Rotate left">
            <IconButton
              aria-label="Rotate left"
              icon={<Icon as={MdRotateLeft} />}
              size="sm"
              onClick={handleRotateLeft}
              mr={1}
            />
          </Tooltip>
          <Tooltip label="Rotate right">
            <IconButton
              aria-label="Rotate right"
              icon={<Icon as={MdRotateRight} />}
              size="sm"
              onClick={handleRotateRight}
              mr={1}
            />
          </Tooltip>
          <Tooltip label="Download">
            <IconButton
              aria-label="Download"
              icon={<Icon as={MdDownload} />}
              size="sm"
              onClick={handleDownload}
            />
          </Tooltip>
        </Flex>

        <Box
          ref={containerRef}
          display="flex"
          alignItems="center"
          justifyContent="center"
          h="full"
          w="full"
          cursor="pointer"
          onClick={onOpen}
        >
          <Box
            style={{
              ...getScaleAndTransform(),
              display: "inline-block"
            }}
          >
            <Image
              ref={imageRef}
              alt="extracted image"
              src={imageUrl}
              objectFit="contain"
              onLoad={handleImageLoad}
            />
          </Box>
        </Box>
      </Box>

      {/* Zoom Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="full">
        <ModalOverlay />
        <ModalContent bg="transparent" boxShadow="none">
          <ModalBody p={0} position="relative">
            {/* Close button */}
            <IconButton
              aria-label="Close zoom"
              icon={<Icon as={MdClose} />}
              position="absolute"
              top={4}
              right={4}
              zIndex={20}
              bg="white"
              color="gray.800"
              _hover={{ bg: "gray.100" }}
              onClick={onClose}
            />

            {/* Rotation controls in zoom */}
            <Flex
              position="absolute"
              top={4}
              left={4}
              bg="white"
              p={1}
              borderRadius="md"
              zIndex={20}
              boxShadow="md"
            >
              <Tooltip label="Rotate left">
                <IconButton
                  aria-label="Rotate left"
                  icon={<Icon as={MdRotateLeft} />}
                  size="sm"
                  onClick={handleRotateLeft}
                  mr={1}
                />
              </Tooltip>
              <Tooltip label="Rotate right">
                <IconButton
                  aria-label="Rotate right"
                  icon={<Icon as={MdRotateRight} />}
                  size="sm"
                  onClick={handleRotateRight}
                />
              </Tooltip>
            </Flex>

            {/* Zoomed image with rotation */}
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              h="100vh"
              w="100vw"
              bg="rgba(0,0,0,0.9)"
            >
              <Box
                style={{
                  ...getZoomTransform(),
                  display: "inline-block"
                }}
              >
                <Image
                  alt="Zoomed image"
                  src={imageUrl}
                  maxW="90vw"
                  maxH="90vh"
                  objectFit="contain"
                />
              </Box>
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};
